/**
 * Charging access outcome (`greencharge.access-outcome.v1`; sw-ev-api
 * docs/CHARGER_ACCESS_OUTCOME.md) — the little the app is allowed to know.
 *
 * The backend decides whether a vehicle may charge at a charger, on what
 * footing, and what the driver may do next. The app prints `title`,
 * `message` and `basePrice.label` and turns `actions` into buttons. It reads
 * the `outcome` only to tell "carry on" from "show the result screen", and
 * never reads a reason code or an access rule: there is no code-to-copy map
 * here and none may be added.
 */

/** Action codes this app can render as a button. */
export const ACCESS_ACTION = Object.freeze({
  CONTINUE_WITH_CARD: 'CONTINUE_WITH_CARD',
  SELECT_ANOTHER_VEHICLE: 'SELECT_ANOTHER_VEHICLE',
  EXIT: 'EXIT',
});

const CARD_FALLBACK_OUTCOME = 'PUBLIC_FALLBACK';
// Nothing was refused: the wizard continues with no extra screen.
const CONTINUING_OUTCOMES = Object.freeze(['FLEET_SESSION', 'PUBLIC_SESSION']);
// Outcomes the result screen renders from the backend's own `actions`.
const RESULT_OUTCOMES = Object.freeze([CARD_FALLBACK_OUTCOME, 'DENIED', 'NO_CHARGING_FLOW']);
// What a denial offers. Also used for an outcome this build does not know:
// the contract says to treat it as a denial and print the title and message.
const DENIED_ACTIONS = Object.freeze([ACCESS_ACTION.SELECT_ANOTHER_VEHICLE, ACCESS_ACTION.EXIT]);
const RENDERABLE = Object.freeze(Object.values(ACCESS_ACTION));

export function isAccessOutcome(value) {
  return Boolean(value) && typeof value === 'object'
    && typeof value.outcome === 'string' && value.outcome.length > 0;
}

/** True when the wizard carries on exactly as before, with no result screen. */
export function accessOutcomeContinues(accessOutcome) {
  return isAccessOutcome(accessOutcome) && CONTINUING_OUTCOMES.includes(accessOutcome.outcome);
}

/**
 * The buttons to offer, in the backend's order.
 *
 * - Paying by card is offered only when the backend lists it AND says the
 *   public fallback is allowed AND the outcome is the card fallback. Any one
 *   missing and there is no payment option.
 * - An outcome this build does not know is treated as a denial.
 * - The driver is never left without a way off the screen.
 */
export function accessResultActions(accessOutcome) {
  const known = isAccessOutcome(accessOutcome) && RESULT_OUTCOMES.includes(accessOutcome.outcome);
  const listed = known && Array.isArray(accessOutcome.actions) ? accessOutcome.actions : DENIED_ACTIONS;
  const cardAllowed = known
    && accessOutcome.outcome === CARD_FALLBACK_OUTCOME
    && accessOutcome.publicFallbackAllowed === true;

  const out = [];
  for (const code of listed) {
    if (!RENDERABLE.includes(code) || out.includes(code)) continue;
    if (code === ACCESS_ACTION.CONTINUE_WITH_CARD && !cardAllowed) continue;
    out.push(code);
  }
  return out.length > 0 ? out : [ACCESS_ACTION.EXIT];
}
